import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Bell, ChevronLeft, X } from 'lucide-react';
import { ActivityItem } from '../types';
import { activityService } from '../services/activityService';
import { openExternalUrl } from '../utils/openExternal';
import { sourceIconOf } from '../utils/systemSource';
import { isDifferentDate } from '../utils/date';
import { SystemNoticeCard } from './chat/SystemNoticeCard';

/**
 * 출처별 봇 대화 — 외부 시스템 알림(전자결재 등)을 채팅처럼 시간순 카드 말풍선으로 보여 준다.
 * (벤치마킹: Slack 앱 Messages 탭 · 카카오워크 결재 봇방 · Teams 결재 카드)
 *
 * ⚠ 채널이 아니다(가상 뷰). channels 배열·activeChannel·localStorage에 넣지 않는다 — 초안 DM과 같은 이유로,
 *   넣는 순간 목록·홈·검색·안읽음·푸시가 유령 방을 다룬다. 데이터는 알림함과 같은
 *   GET /api/activity?tab=<source>이고 실시간은 activity:new / activity:read window 이벤트다.
 *   그래서 서버·소켓 계약 변경이 없다.
 *
 * 읽음: 알림함 목록에서 누른 알림(=포커스 대상)과 '문서 열기'를 누른 카드만 읽음 처리한다.
 * 대화를 열었다는 이유로 전부 읽음 처리하지 않는다 — 결재 요청의 안 읽음은 '할 일' 표시이기도 하다.
 * 삭제는 알림함에서만 한다 — 이 화면은 activity:read의 removedId·removedRead를 받아 카드를 뺄 뿐이다.
 */

const PAGE_SIZE = 50;
// 포커스 대상이 첫 페이지에 없을 때 더 불러오는 상한(50 × 6 = 300건). 넘으면 최신 위치로 연다
const MAX_FOCUS_PAGES = 6;
const GROUP_WINDOW_MS = 5 * 60 * 1000;
const HIGHLIGHT_MS = 2000;
const NEAR_BOTTOM_PX = 120;

interface SystemFeedViewProps {
  source: string;
  /** 열 때 스크롤·강조할 알림 id (없으면 최신 위치) */
  focusId?: number | null;
  /** 같은 대화를 연 채로 다른 알림을 눌러도 다시 포커스하게 하는 카운터 */
  focusSeq: number;
  onClose: () => void;
  /** 모바일 전체화면 — 헤더 왼쪽 뒤로가기, 데스크톱은 오른쪽 닫기 */
  isMobile: boolean;
}

type PendingScroll = { kind: 'bottom' } | { kind: 'focus'; id: number } | { kind: 'anchor'; prevHeight: number; prevTop: number };

export const SystemFeedView: React.FC<SystemFeedViewProps> = ({ source, focusId, focusSeq, onClose, isMobile }) => {
  // 화면 순서(위=과거, 아래=최신). 서버는 id DESC로 주므로 뒤집어 담는다
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  // 데이터 변화 없이 스크롤만 다시 걸어야 할 때 렌더를 한 번 일으키는 카운터
  const [, setScrollTick] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<number, HTMLDivElement>());
  const pendingScrollRef = useRef<PendingScroll | null>(null);
  const itemsRef = useRef<ActivityItem[]>([]);
  itemsRef.current = items;
  const loadedSourceRef = useRef<string | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const registerRef = useCallback((id: number, el: HTMLDivElement | null) => {
    if (el) itemRefs.current.set(id, el);
    else itemRefs.current.delete(id);
  }, []);

  const markItemsRead = useCallback((pred: (it: ActivityItem) => boolean) => {
    const now = new Date().toISOString();
    setItems(prev => prev.map(it => (!it.readAt && pred(it) ? { ...it, readAt: now } : it)));
  }, []);

  const markOneRead = useCallback((item: ActivityItem) => {
    if (item.readAt) return;
    // fire-and-forget — 알림함 목록·벨 배지는 서버의 activity:read 에코가 맞춘다(멀티세션 일관)
    activityService.markRead(item.id).catch(() => {});
    markItemsRead(it => it.id === item.id);
  }, [markItemsRead]);

  // 열기 / 출처 전환 / 다른 알림 포커스 요청
  useEffect(() => {
    const target = focusId != null ? Number(focusId) : null;

    // 같은 대화가 이미 열려 있고 대상이 로드 창 안에 있으면 다시 받지 않고 스크롤만
    if (loadedSourceRef.current === source && target != null) {
      const hit = itemsRef.current.find(it => it.id === target);
      if (hit) {
        pendingScrollRef.current = { kind: 'focus', id: target };
        setHighlightId(target);
        setScrollTick(t => t + 1);
        markOneRead(hit);
        return;
      }
    }

    let cancelled = false;
    setError(null);
    if (loadedSourceRef.current !== source) {
      setLoaded(false);
      setItems([]);
    }
    (async () => {
      try {
        let acc: ActivityItem[] = [];
        let more = false;
        let before: number | undefined;
        for (let page = 0; page < MAX_FOCUS_PAGES; page++) {
          const res = await activityService.getActivity(source, before, PAGE_SIZE);
          acc = acc.concat(res.items);
          more = res.hasMore;
          if (target == null || acc.some(it => it.id === target) || !more || acc.length === 0) break;
          before = acc[acc.length - 1].id;
        }
        if (cancelled) return;
        const focused = target != null ? acc.find(it => it.id === target) : undefined;
        loadedSourceRef.current = source;
        pendingScrollRef.current = focused ? { kind: 'focus', id: focused.id } : { kind: 'bottom' };
        setItems(acc.slice().reverse());
        setHasMore(more);
        setLoaded(true);
        if (focused) {
          setHighlightId(focused.id);
          markOneRead(focused);
        }
      } catch (err) {
        console.error('Failed to fetch system feed:', err);
        if (!cancelled) {
          setError('알림을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
          setLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
    // focusSeq가 바뀔 때마다(같은 id를 다시 눌러도) 포커스를 다시 건다. focusId는 seq와 함께 바뀐다
  }, [source, focusSeq]);

  // 강조는 잠깐만 — 사라지는 것은 transition-colors가 부드럽게 처리한다(모션 감소 설정 시 즉시)
  useEffect(() => {
    if (highlightId == null) return;
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, [highlightId, focusSeq]);

  // 커밋 직후 스크롤 — 하단 고정 / 포커스 / 과거 로드 앵커링
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const pending = pendingScrollRef.current;
    if (!el || !pending) return;
    pendingScrollRef.current = null;
    if (pending.kind === 'bottom') {
      el.scrollTop = el.scrollHeight;
    } else if (pending.kind === 'anchor') {
      el.scrollTop = el.scrollHeight - pending.prevHeight + pending.prevTop;
    } else {
      itemRefs.current.get(pending.id)?.scrollIntoView({ block: 'center' });
    }
  });

  // 실시간 — 알림함과 같은 CustomEvent를 구독한다(socketService 재발행)
  useEffect(() => {
    const onNew = (e: Event) => {
      const item = (e as CustomEvent).detail as ActivityItem;
      if (!item?.id || item.source !== source) return;
      const el = scrollRef.current;
      const nearBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
      setItems(prev => {
        if (prev.some(it => it.id === item.id)) return prev;
        if (nearBottom) pendingScrollRef.current = { kind: 'bottom' };
        return [...prev, item];
      });
    };
    const onRead = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        id?: number; all?: boolean; tab?: string; removedId?: number; removedRead?: boolean;
      };
      if (detail?.removedRead) {
        // 알림함에서 '읽은 알림 삭제' — 이 출처(또는 전체) 탭이면 읽은 카드를 뺀다
        const tab = detail.tab || 'all';
        if (tab === 'all' || tab === source) setItems(prev => prev.filter(it => !it.readAt));
      } else if (detail?.removedId) {
        // 알림함에서 그 알림을 지웠다(데스크톱은 봇 대화를 띄운 채 가운데 알림함에서 지운다)
        const id = Number(detail.removedId);
        setItems(prev => (prev.some(it => it.id === id) ? prev.filter(it => it.id !== id) : prev));
      } else if (detail?.all) {
        const tab = detail.tab || 'all';
        if (tab === 'all' || tab === source) markItemsRead(() => true);
      } else if (detail?.id) {
        const id = Number(detail.id);
        markItemsRead(it => it.id === id);
      }
    };
    window.addEventListener('activity:new', onNew);
    window.addEventListener('activity:read', onRead);
    return () => {
      window.removeEventListener('activity:new', onNew);
      window.removeEventListener('activity:read', onRead);
    };
  }, [source, markItemsRead]);

  const loadOlder = useCallback(async () => {
    const el = scrollRef.current;
    const oldest = itemsRef.current[0];
    if (!el || !oldest || loadingOlder || !hasMore) return;
    setLoadingOlder(true);
    const requestedSource = source;
    try {
      const res = await activityService.getActivity(source, oldest.id, PAGE_SIZE);
      if (loadedSourceRef.current !== requestedSource) return;
      pendingScrollRef.current = { kind: 'anchor', prevHeight: el.scrollHeight, prevTop: el.scrollTop };
      setItems(prev => {
        const seen = new Set(prev.map(it => it.id));
        return [...res.items.filter(it => !seen.has(it.id)).reverse(), ...prev];
      });
      setHasMore(res.hasMore);
    } catch (err) {
      console.error('Failed to load older system feed:', err);
    } finally {
      setLoadingOlder(false);
    }
  }, [source, loadingOlder, hasMore]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el && el.scrollTop < 80) void loadOlder();
  }, [loadOlder]);

  const handleOpenDocument = useCallback((item: ActivityItem) => {
    markOneRead(item);
    if (item.link) void openExternalUrl(item.link);
  }, [markOneRead]);

  // 그룹핑·날짜 구분선 — ChatWindow의 continuationIds와 같은 규칙(같은 날 5분 이내 연속)
  const layout = useMemo(() => items.map((it, i) => {
    const prev = items[i - 1];
    const newDay = !prev || isDifferentDate(prev.createdAt, it.createdAt);
    const continuation = !!prev && !newDay
      && new Date(it.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_WINDOW_MS;
    return { newDay, continuation };
  }), [items]);

  const label = items[items.length - 1]?.actorName || items[0]?.actorName || '알림';
  const Icon = sourceIconOf(source);

  return (
    <div className="flex flex-col h-full w-full min-w-0 bg-slate-50">
      <header className="h-14 md:h-16 border-b border-slate-200 bg-white flex items-center px-3 md:px-6 justify-between shrink-0">
        <div className="flex items-center min-w-0">
          {isMobile && (
            <button
              className="p-2 -ml-1 mr-1 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              onClick={onClose}
              aria-label="알림함으로 돌아가기"
            >
              <ChevronLeft size={22} />
            </button>
          )}
          <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-blue-100 text-blue-600 mr-3">
            <Icon size={18} />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-800 truncate">{label}</h2>
            <p className="text-xs text-slate-400 truncate">알림 전용 대화</p>
          </div>
        </div>
        {!isMobile && (
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            title="닫기"
            aria-label="닫기"
          >
            <X size={20} />
          </button>
        )}
      </header>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 md:px-6 py-4"
        role="log"
        aria-label={`${label} 알림`}
      >
        {!loaded ? (
          <div className="flex items-center justify-center h-32 text-sm text-slate-400">불러오는 중...</div>
        ) : error && items.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-brand-600">{error}</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-300">
            <Bell size={36} />
            <p className="text-sm text-slate-400">아직 받은 알림이 없습니다</p>
            <p className="text-xs text-slate-300">새 알림이 오면 여기에 모입니다</p>
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="flex justify-center pb-3">
                <button
                  onClick={() => void loadOlder()}
                  disabled={loadingOlder}
                  className="px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-100 rounded-full transition-colors disabled:opacity-50"
                >
                  {loadingOlder ? '불러오는 중...' : '이전 알림 더 보기'}
                </button>
              </div>
            )}
            {items.map((it, i) => (
              <SystemNoticeCard
                key={it.id}
                item={it}
                isContinuation={layout[i].continuation}
                showDateSeparator={layout[i].newDay}
                isHighlighted={highlightId === it.id}
                onOpenDocument={handleOpenDocument}
                registerRef={registerRef}
              />
            ))}
          </>
        )}
      </div>

      <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-3 text-center text-xs text-slate-400 safe-area-bottom">
        알림 전용 대화라 답장할 수 없습니다
      </div>
    </div>
  );
};
